import { useBusinessContext } from '@root/context'
import {
  ChangeLogAction,
  ChangeLogListParams,
  ChangeLogResourceType,
  fetchChangeLog,
  fetchChangeLogActors
} from '@root/query/changeLog'
import { useQuery } from '@tanstack/react-query'

const CHANGE_LOG_STALE_TIME_MS = 30_000

interface UseChangeLogProps {
  resourceType?: ChangeLogResourceType | 'all'
  action?: ChangeLogAction | 'all'
  from?: string
  to?: string
  actorId?: number | null
  search?: string
  page?: number
  pageSize?: number
}

export const useChangeLog = ({
  resourceType = 'all',
  action = 'all',
  from,
  to,
  actorId,
  search,
  page = 1,
  pageSize = 25
}: UseChangeLogProps = {}) => {
  const { slug } = useBusinessContext()

  const trimmedSearch = search?.trim()

  const params: ChangeLogListParams = {
    resource_type: resourceType,
    page,
    page_size: pageSize,
    ...(action !== 'all' ? { action } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(actorId ? { actor_id: actorId } : {}),
    ...(trimmedSearch ? { search: trimmedSearch } : {})
  }

  return useQuery({
    queryKey: [slug, 'change-log', params],
    queryFn: () => fetchChangeLog({ slug }, params),
    placeholderData: (previousData, previousQuery) => {
      // Avoid showing stale wrong-page rows when the requested page is beyond
      // the previous result set (e.g. total shrunk and page was clamped).
      if (previousData && previousQuery) {
        const previousParams = previousQuery.queryKey[2] as ChangeLogListParams | undefined
        const previousPage = previousParams?.page ?? 1
        if (previousPage !== page) {
          const previousTotalPages = Math.max(
            1,
            Math.ceil(previousData.count / (previousParams?.page_size ?? pageSize))
          )
          if (page > previousTotalPages) {
            return undefined
          }
        }
      }
      return previousData
    },
    staleTime: CHANGE_LOG_STALE_TIME_MS
  })
}

export const useChangeLogActors = () => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: [slug, 'change-log', 'actors'],
    queryFn: () => fetchChangeLogActors({ slug }),
    staleTime: CHANGE_LOG_STALE_TIME_MS
  })
}
